import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { label: '首页', to: '/' },
  { label: '作品', to: '/works' },
  { label: '明星资料', to: '/profile' },
  { label: '动态', to: '/news' },
  { label: '关于', to: '/about' },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword('');
    setMenuOpen(false);
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    [
      'relative px-1 py-2 text-sm transition-colors whitespace-nowrap',
      isActive
        ? 'text-primary-700 font-medium'
        : 'text-foreground-700 hover:text-primary-700',
    ].join(' ');

  return (
    <header
      className={[
        'sticky top-0 z-50 w-full border-b transition-colors',
        scrolled
          ? 'bg-background-50/95 border-background-200 backdrop-blur'
          : 'bg-background-50 border-background-200/70',
      ].join(' ')}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-700 text-background-50">
              <i className="ri-film-line text-lg" aria-hidden="true"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-semibold tracking-wide text-foreground-950">
                吉泽明步
              </span>
              <span className="mt-0.5 text-[10px] uppercase tracking-[0.25em] text-foreground-500">
                Akiho Yoshizawa
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} className={linkClass} end={item.to === '/'}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form onSubmit={submitSearch} className="hidden md:flex items-center rounded-full border border-background-300/70 bg-background-100 pl-3 pr-1 py-1">
              <i className="ri-search-line text-foreground-500 text-sm" aria-hidden="true"></i>
              <input
                type="text"
                name="q"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索作品 / 动态"
                aria-label="站内搜索"
                className="w-32 lg:w-40 bg-transparent px-2 py-1 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
              <button
                type="submit"
                className="hidden xl:inline-flex items-center justify-center rounded-full bg-primary-600 px-3 py-1 text-xs text-background-50 whitespace-nowrap hover:bg-primary-700 transition-colors cursor-pointer"
              >
                搜索
              </button>
            </form>

            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="打开菜单"
              aria-expanded={menuOpen}
              className="lg:hidden flex h-9 w-9 items-center justify-center rounded-md border border-background-300/70 text-foreground-800 cursor-pointer"
            >
              <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 animate-fadeIn">
          <div className="w-full px-4 py-4 md:px-8">
            <form onSubmit={submitSearch} className="mb-4 flex items-center rounded-full border border-background-300/70 bg-background-100 pl-3 pr-2 py-1.5">
              <i className="ri-search-line text-foreground-500 text-sm" aria-hidden="true"></i>
              <input
                type="text"
                name="q"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索作品 / 动态"
                aria-label="站内搜索"
                className="flex-1 bg-transparent px-2 py-1 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
              <button type="submit" className="rounded-full bg-primary-600 px-3 py-1 text-xs text-background-50 whitespace-nowrap cursor-pointer">
                搜索
              </button>
            </form>
            <nav className="flex flex-col">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    [
                      'flex items-center justify-between rounded-md px-3 py-3 text-sm',
                      isActive ? 'bg-primary-50 text-primary-700 font-medium' : 'text-foreground-700',
                    ].join(' ')
                  }
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true"></i>
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}